// =============================================================================
// Admin Analytics — export every row of a drilldown
// =============================================================================
// The dialog only ever holds the page on screen. Export walks the same rows
// query — the same filters, selections, date range and sort — one
// `EXPORT_PAGE_LIMIT` page at a time, with at most `EXPORT_PAGES_IN_FLIGHT`
// requests open, and hands back every row in order. Only the first page asks
// for the total; later pages skip the count. Each page is capped by the server,
// so this stays a bounded walk rather than the old fire-every-page-at-once
// fan-out.
//
// A file is all or nothing: a failed page, an error anywhere in a page, or a
// row count that no longer matches the total fails the export rather than
// writing a partial CSV. Closing the dialog, unmounting it, or changing what's
// being exported cancels a running export.
// =============================================================================
import { Variables } from "graphql-request";
import { useEffect, useState } from "react";

import { usePartialDataFetcher } from "@pages/AdminAnalytics/data/usePartialDataFetcher";

/** Rows per export request — the server's page cap. */
export const EXPORT_PAGE_LIMIT = 200;
/** Export page requests open at once, so an export never crowds the shared database. */
export const EXPORT_PAGES_IN_FLIGHT = 2;

export type DrilldownExportState =
  | { status: "idle" }
  | { status: "exporting"; fetched: number; total: number | undefined }
  | { status: "error" };

export interface DrilldownExport<TRow> {
  state: DrilldownExportState;
  /** Fetches every row of the current result; resolves to `undefined` if it failed or was cancelled. */
  run: () => Promise<TRow[] | undefined>;
}

interface RowsPage<TRow> {
  /** Only requested on the first page. */
  totalCount?: number;
  rows: readonly TRow[];
}

interface DrilldownExportArgs<TData, TVariables, TRow> {
  /** The drilldown's rows operation — the same one the table pages through. */
  document: string;
  /** The rows query's variables, without `page` or `withTotal`. */
  variables: Omit<TVariables, "page" | "withTotal">;
  /** Whether the dialog is open; closing it cancels a running export. */
  open: boolean;
  pageOf: (data: TData | undefined) => RowsPage<TRow> | null | undefined;
}

class ExportFailed extends Error {}

const IDLE: DrilldownExportState = { status: "idle" };

export const useDrilldownExport = <TData extends { adminAnalytics?: unknown }, TVariables extends Variables, TRow>({
  document,
  variables,
  open,
  pageOf,
}: DrilldownExportArgs<TData, TVariables, TRow>): DrilldownExport<TRow> => {
  const fetchPartial = usePartialDataFetcher<TData, TVariables>(document);
  const [state, setState] = useState<DrilldownExportState>(IDLE);
  // The running export's controller is state, not a ref: the cleanup below
  // aborts it whenever it's replaced, the dialog closes or unmounts, or the
  // result being exported stops being the one on screen.
  const [controller, setController] = useState<AbortController | undefined>(undefined);
  const variablesKey = JSON.stringify(variables);
  useEffect(() => () => controller?.abort(), [controller, variablesKey, open]);

  const fetchPage = async (offset: number, signal: AbortSignal): Promise<RowsPage<TRow>> => {
    const result = await fetchPartial(
      { ...variables, page: { offset, limit: EXPORT_PAGE_LIMIT }, withTotal: offset === 0 } as unknown as TVariables,
      signal,
    );
    const page = result.requestFailed || result.hadErrors ? undefined : pageOf(result.data);
    if (!page) throw new ExportFailed();
    return page;
  };

  const run = async (): Promise<TRow[] | undefined> => {
    const ctrl = new AbortController();
    setController(ctrl);
    setState({ status: "exporting", fetched: 0, total: undefined });
    try {
      const first = await fetchPage(0, ctrl.signal);
      const total = first.totalCount;
      if (total === undefined) throw new ExportFailed();
      let fetched = first.rows.length;
      setState({ status: "exporting", fetched, total });

      const offsets: number[] = [];
      for (let offset = EXPORT_PAGE_LIMIT; offset < total; offset += EXPORT_PAGE_LIMIT) offsets.push(offset);
      const pages: (readonly TRow[])[] = new Array(offsets.length);
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < offsets.length) {
          const index = nextIndex++;
          const page = await fetchPage(offsets[index], ctrl.signal);
          pages[index] = page.rows;
          fetched += page.rows.length;
          setState({ status: "exporting", fetched, total });
        }
      };
      await Promise.all(Array.from({ length: Math.min(EXPORT_PAGES_IN_FLIGHT, offsets.length) }, worker));

      const rows = [...first.rows, ...pages.flat()];
      // Pages come back in the server's stable order, so a count that no longer
      // matches the total means the data changed mid-export (a fact refresh
      // landed). Retrying beats a file that's quietly a few rows off.
      if (rows.length !== total) throw new ExportFailed();
      setState(IDLE);
      return rows;
    } catch {
      const cancelled = ctrl.signal.aborted;
      // One page failing stops the other page still in flight.
      ctrl.abort();
      setState(cancelled ? IDLE : { status: "error" });
      return undefined;
    } finally {
      setController(undefined);
    }
  };

  return { state, run };
};
